import React, { useEffect, useState } from 'react';
import Banner, { BannerVariant } from 'components/styleguide/banner/Banner';
import {
  WageAdvisorBackendState,
  WageAdvisorInput,
  WageAdvisorOfficeLocation,
  useWageAdvisor,
} from './use_wage_advisor';

const styles = require('./wage_advisor.module.scss');

export interface WageAdvisorMessages {
  belowTitle: string;
  belowDescription: string;
  withinTitle: string;
  withinDescription: string;
  aboveTitle: string;
  aboveDescription: string;
  range: string;
  reportLink: string;
}

const DEFAULT_MESSAGES: WageAdvisorMessages = {
  belowTitle: 'Below market range',
  belowDescription: 'This salary is %{percent}% below market and could reduce candidate volume and alignment.',
  withinTitle: 'Within market range',
  withinDescription: 'This salary is aligned with the market and should attract relevant candidates for this role.',
  aboveTitle: 'Strong candidate attraction expected',
  aboveDescription: 'This salary is %{percent}% above market and is likely to attract more candidates and stronger profiles.',
  range: 'Range for %{experienceLevel} %{role} in %{country} is %{min} - %{max}',
  reportLink: 'Report %{year}',
};

const VARIANT_BY_STATE: Record<WageAdvisorBackendState, BannerVariant> = {
  BELOW_MARKET: 'warning',
  WITHIN_MARKET: 'info',
  ABOVE_MARKET: 'success',
};

const ICON_BY_STATE: Record<WageAdvisorBackendState, string> = {
  BELOW_MARKET: 'trendingDown',
  WITHIN_MARKET: 'thumbsUp',
  ABOVE_MARKET: 'trendingUpArrow',
};

export interface WageAdvisorProps {
  categoryId?: string | number | null;
  experienceLevel?: number | null;
  grossSalaryLow?: number | null;
  grossSalaryHigh?: number | null;
  currencyCode?: string | null;
  workModel?: string | null;
  officeLocations?: WageAdvisorOfficeLocation[];
  eligibleLocations?: string[];
  topGeographies?: string[];
  // The employer's own registered country. Static for the lifetime of the
  // form (there's no field for it), so it's prop-only — no DOM selector.
  companyCountryCode?: string | null;
  messages?: Partial<WageAdvisorMessages>;
  // DOM selectors used to subscribe to live form changes. Optional —
  // when omitted, the component is fully controlled by props.
  categorySelector?: string;
  salaryLowSelector?: string;
  salaryHighSelector?: string;
  currencySelector?: string;
  experienceLevelSelector?: string;
  workModelSelectors?: string[];
  // Optional translation applied to the value read from workModelSelectors,
  // e.g. mapping the v2 edit-form radio values (remote/hybrid/onsite) to the
  // legacy work-model symbols the backend visibility gate understands. Values
  // not in the map pass through unchanged.
  workModelValueMap?: Record<string, string>;
  // Reads a JSON-encoded array of office locations from a hidden input.
  officeLocationsSelector?: string;
  // Reads the eligible-location codes from the SelectWithTags hidden inputs.
  eligibleLocationsSelector?: string;
  // Reads the selected option values from a multi-select element.
  topGeographiesSelector?: string;
}

function parseIntOrNull(value: string | undefined | null): number | null {
  if (value === undefined || value === null || value === '') return null;
  const cleaned = String(value).replace(/[^0-9-]/g, '');
  if (cleaned === '' || cleaned === '-') return null;
  const parsed = parseInt(cleaned, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function getElValue(selector?: string): string | null {
  if (!selector) return null;
  const el = document.querySelector(selector) as HTMLInputElement | HTMLSelectElement | null;
  return el ? el.value : null;
}

function readOfficeLocationsFromHidden(selector?: string): WageAdvisorOfficeLocation[] | null {
  if (!selector) return null;
  const el = document.querySelector(selector) as HTMLInputElement | null;
  if (!el?.value) return [];
  // Returns null (rather than []) when the value can't be parsed, so callers
  // keep the previously known locations instead of wiping a valid state. The
  // server-rendered initial value isn't always valid JSON; the live form
  // serializes proper JSON on change.
  try {
    const parsed: unknown = JSON.parse(el.value);
    if (!Array.isArray(parsed)) return null;
    return parsed.map((entry: unknown) => {
      const record = (entry && typeof entry === 'object' ? entry : {}) as Record<string, unknown>;
      const city = record.city;
      const countryCode = record.countryCode ?? record.country_code;
      return {
        city: typeof city === 'string' ? city : null,
        countryCode: typeof countryCode === 'string' ? countryCode : null,
      };
    });
  } catch {
    return null;
  }
}

function readEligibleLocationsFromInputs(selector?: string): string[] | null {
  if (!selector) return null;
  const els = document.querySelectorAll(selector);
  // Returns null (rather than []) when the field isn't on the page (e.g. the
  // wizard renders eligible locations on a different step), so callers keep the
  // prop-provided value instead of wiping it. SelectWithTags always renders a
  // blank sentinel input when present, so a match means the field is live.
  if (els.length === 0) return null;
  return Array.from(els)
    .map((el) => (el as HTMLInputElement).value)
    .filter((v) => v && v !== '');
}

function readTopGeographiesFromSelect(selector?: string): string[] | null {
  if (!selector) return null;
  const el = document.querySelector(selector) as HTMLSelectElement | null;
  if (!el) return [];
  return Array.from(el.selectedOptions || []).map((opt) => opt.value).filter((v) => v && v !== '');
}

function getCheckedWorkModel(
  selectors: string[] | undefined,
  valueMap?: Record<string, string>
): string | null {
  if (!selectors || selectors.length === 0) return null;
  for (const selector of selectors) {
    const el = document.querySelector(selector) as HTMLInputElement | null;
    if (el?.checked) return valueMap?.[el.value] ?? el.value;
  }
  return null;
}

function formatMessage(template: string, vars: Record<string, string | number>): string {
  return template.replace(/%\{(\w+)\}/g, (_match, key) =>
    vars[key] !== undefined ? String(vars[key]) : ''
  );
}

function isSafeHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

const WageAdvisor: React.FC<WageAdvisorProps> = (props) => {
  const messages: WageAdvisorMessages = { ...DEFAULT_MESSAGES, ...(props.messages || {}) };

  const [liveInput, setLiveInput] = useState<WageAdvisorInput>(() => ({
    categoryId: props.categoryId,
    experienceLevel: props.experienceLevel,
    grossSalaryLow: props.grossSalaryLow,
    grossSalaryHigh: props.grossSalaryHigh,
    currencyCode: props.currencyCode,
    workModel: props.workModel,
    officeLocations: props.officeLocations || [],
    eligibleLocations: props.eligibleLocations || [],
    topGeographies: props.topGeographies || [],
    companyCountryCode: props.companyCountryCode,
  }));

  useEffect(() => {
    const readDom = () => {
      setLiveInput((prev) => {
        const next: WageAdvisorInput = { ...prev };

        if (props.categorySelector) {
          const v = getElValue(props.categorySelector);
          next.categoryId = v && v !== '' ? v : null;
        }
        if (props.salaryLowSelector) {
          next.grossSalaryLow = parseIntOrNull(getElValue(props.salaryLowSelector));
        }
        if (props.salaryHighSelector) {
          next.grossSalaryHigh = parseIntOrNull(getElValue(props.salaryHighSelector));
        }
        if (props.currencySelector) {
          const v = getElValue(props.currencySelector);
          next.currencyCode = v && v !== '' ? v : null;
        }
        if (props.experienceLevelSelector) {
          const el = document.querySelector(
            `${props.experienceLevelSelector}:checked`
          ) as HTMLInputElement | null;
          next.experienceLevel = el ? parseIntOrNull(el.value) : prev.experienceLevel ?? null;
        }
        if (props.workModelSelectors && props.workModelSelectors.length > 0) {
          next.workModel = getCheckedWorkModel(props.workModelSelectors, props.workModelValueMap) ?? null;
        }
        if (props.officeLocationsSelector) {
          const locs = readOfficeLocationsFromHidden(props.officeLocationsSelector);
          if (locs !== null) next.officeLocations = locs;
        }
        if (props.eligibleLocationsSelector) {
          const eligible = readEligibleLocationsFromInputs(props.eligibleLocationsSelector);
          if (eligible !== null) next.eligibleLocations = eligible;
        }
        if (props.topGeographiesSelector) {
          const geos = readTopGeographiesFromSelect(props.topGeographiesSelector);
          if (geos !== null) next.topGeographies = geos;
        }
        return next;
      });
    };

    // Prime once on mount to pick up server-rendered values.
    readDom();

    const selectors = [
      props.categorySelector,
      props.salaryLowSelector,
      props.salaryHighSelector,
      props.currencySelector,
      props.experienceLevelSelector,
      ...(props.workModelSelectors || []),
      props.officeLocationsSelector,
      props.eligibleLocationsSelector,
      props.topGeographiesSelector,
    ].filter(Boolean) as string[];

    const handlers: Array<{ el: Element; type: string; fn: EventListener }> = [];
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        const inputFn: EventListener = () => readDom();
        const changeFn: EventListener = () => readDom();
        el.addEventListener('input', inputFn);
        el.addEventListener('change', changeFn);
        handlers.push({ el, type: 'input', fn: inputFn });
        handlers.push({ el, type: 'change', fn: changeFn });
      });
    });

    return () => {
      handlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
    };
  }, [
    props.categorySelector,
    props.salaryLowSelector,
    props.salaryHighSelector,
    props.currencySelector,
    props.experienceLevelSelector,
    props.workModelSelectors,
    props.workModelValueMap,
    props.officeLocationsSelector,
    props.eligibleLocationsSelector,
    props.topGeographiesSelector,
  ]);

  // Mirror prop-driven values into liveInput for any field that does NOT
  // have a DOM selector. DOM selectors, when provided, are the source of
  // truth for that field and overwrite this on the next input/change event.
  useEffect(() => {
    setLiveInput((prev) => ({
      ...prev,
      categoryId: props.categorySelector ? prev.categoryId : props.categoryId,
      experienceLevel: props.experienceLevelSelector ? prev.experienceLevel : props.experienceLevel,
      grossSalaryLow: props.salaryLowSelector ? prev.grossSalaryLow : props.grossSalaryLow,
      grossSalaryHigh: props.salaryHighSelector ? prev.grossSalaryHigh : props.grossSalaryHigh,
      currencyCode: props.currencySelector ? prev.currencyCode : props.currencyCode,
      workModel: props.workModelSelectors && props.workModelSelectors.length > 0 ? prev.workModel : props.workModel,
      officeLocations: props.officeLocationsSelector ? prev.officeLocations : (props.officeLocations || prev.officeLocations),
      eligibleLocations: props.eligibleLocationsSelector ? prev.eligibleLocations : (props.eligibleLocations || prev.eligibleLocations),
      topGeographies: props.topGeographiesSelector ? prev.topGeographies : (props.topGeographies || prev.topGeographies),
      companyCountryCode: props.companyCountryCode,
    }));
  }, [
    props.categoryId,
    props.experienceLevel,
    props.grossSalaryLow,
    props.grossSalaryHigh,
    props.currencyCode,
    props.workModel,
    props.officeLocations,
    props.eligibleLocations,
    props.topGeographies,
    props.companyCountryCode,
    props.categorySelector,
    props.experienceLevelSelector,
    props.salaryLowSelector,
    props.salaryHighSelector,
    props.currencySelector,
    props.workModelSelectors,
    props.officeLocationsSelector,
    props.eligibleLocationsSelector,
    props.topGeographiesSelector,
  ]);

  const { state, data } = useWageAdvisor(liveInput);

  if (state === 'hidden' || !data) {
    return null;
  }

  const variant = VARIANT_BY_STATE[data.state];
  const icon = ICON_BY_STATE[data.state];
  const percent = Math.abs(data.percentDifference);

  let title: string;
  let description: string;
  switch (data.state) {
    case 'BELOW_MARKET':
      title = messages.belowTitle;
      description = formatMessage(messages.belowDescription, { percent });
      break;
    case 'ABOVE_MARKET':
      title = messages.aboveTitle;
      description = formatMessage(messages.aboveDescription, { percent });
      break;
    case 'WITHIN_MARKET':
    default:
      title = messages.withinTitle;
      description = messages.withinDescription;
      break;
  }

  const rangeText = formatMessage(messages.range, {
    experienceLevel: data.experienceLevelLabel,
    role: data.role,
    country: data.country,
    min: `${data.benchmarkMin}€`,
    max: `${data.benchmarkMax}€`,
  });
  const reportText = formatMessage(messages.reportLink, { year: data.editionYear });

  const note = (
    <span className={styles.note}>
      {rangeText}
      {data.reportUrl && isSafeHttpUrl(data.reportUrl) && (
        <>
          {' • '}
          <a
            className={styles.reportLink}
            href={data.reportUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {reportText}
          </a>
        </>
      )}
    </span>
  );

  return (
    <div className={styles.container}>
      <Banner variant={variant} icon={icon} title={title} description={description} note={note} />
    </div>
  );
};

export default WageAdvisor;
